import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Instagram, FileText, Wrench, ArrowRight, Sparkles, Mail, Image as ImageIcon, Video, Code, Youtube, Crown, Lock, Music } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const ICONS = { instagram: Instagram, "file-text": FileText, mail: Mail, image: ImageIcon, video: Video, youtube: Youtube, music: Music };
const ACCENT = {
  "instagram-post-maker": { grad: "from-[#E1306C] to-[#F77737]", ring: "border-[#E1306C]/40", text: "text-[#f48fb1]" },
  "cv-creator": { grad: "from-emerald-500 to-teal-400", ring: "border-emerald-400/40", text: "text-emerald-300" },
  "cover-letter": { grad: "from-sky-500 to-indigo-400", ring: "border-sky-400/40", text: "text-sky-300" },
  "animate-image": { grad: "from-[#00F0FF] to-[#7C5CFF]", ring: "border-[#00F0FF]/40", text: "text-[#00F0FF]" },
  "animate-video": { grad: "from-[#7C5CFF] to-[#FF4D8D]", ring: "border-[#7C5CFF]/40", text: "text-[#b79bff]" },
  "youtube-thumbnail": { grad: "from-[#FF3B3B] to-[#FF8A3D]", ring: "border-[#FF3B3B]/40", text: "text-[#ff7a7a]" },
  "ai-music": { grad: "from-[#C8FF4D] to-[#7AD7FF]", ring: "border-[#C8FF4D]/40", text: "text-[#C8FF4D]" },
};

export default function Tools() {
  const navigate = useNavigate();
  const [tools, setTools] = useState(null);

  useEffect(() => { api.toolsList().then((d) => setTools(d.tools || [])).catch(() => setTools([])); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="mx-auto max-w-[1200px] px-5 py-12 sm:px-6">
        <div className="mb-10 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[12px] font-semibold text-emerald-300"><Sparkles size={13} /> Tools</span>
          <h1 className="mt-4 font-display text-4xl font-black tracking-tight sm:text-5xl">Check out our tools</h1>
          <p className="mt-3 text-base text-white/50">A growing suite of AI-powered creation tools — beyond the caption editor. Pick one and get started in seconds.</p>
        </div>

        {tools === null ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-emerald-400" /></div>
        ) : tools.length === 0 ? (
          <div className="py-24 text-center text-white/40" data-testid="tools-empty">No tools are available right now.</div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="tools-grid">
            {tools.map((t) => {
              const Icon = ICONS[t.icon] || Wrench;
              const a = ACCENT[t.slug] || { grad: "from-white/20 to-white/10", ring: "border-white/20", text: "text-white" };
              return (
                <div key={t.slug} data-testid={`tool-card-${t.slug}`}
                  className={`group relative flex flex-col overflow-hidden rounded-2xl border ${a.ring} bg-white/[0.03] p-6 transition-all hover:bg-white/[0.06] ${!t.enabled ? "opacity-60" : ""}`}>
                  <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${a.grad} text-black`}><Icon size={22} /></div>
                  {t.pro && <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-black text-[#F59E0B]" data-testid={`tool-pro-${t.slug}`}>{t.locked ? <Lock size={10} /> : <Crown size={10} />} PRO</span>}
                  <h2 className="text-lg font-bold text-white">{t.name}</h2>
                  <p className="mt-1.5 flex-1 text-[13.5px] leading-relaxed text-white/50">{t.description}</p>
                  <button
                    onClick={() => { if (t.enabled) { trackEvent({ eventType: "tool_open", page: "Tools Hub", feature: t.name, action: `Opened ${t.name}`, target: t.slug }); navigate(t.route); } }}
                    disabled={!t.enabled}
                    data-testid={`tool-open-${t.slug}`}
                    className={`mt-5 inline-flex items-center justify-center gap-1.5 self-start rounded-full bg-gradient-to-r ${a.grad} px-4 py-2 text-[13px] font-bold text-black transition-transform group-hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60`}>
                    {!t.enabled ? "Coming soon" : t.locked ? "Unlock with Pro" : "Open Tool"} {t.enabled && <ArrowRight size={15} />}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 flex flex-col items-start justify-between gap-5 rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-br from-[#00F0FF]/[0.07] to-transparent p-7 sm:flex-row sm:items-center" data-testid="developer-api-promo">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1 text-[12px] font-semibold text-[#00F0FF]"><Code size={13} /> Developer Platform</span>
            <h2 className="mt-3 font-display text-2xl font-black">CaptionMotion AI Image API</h2>
            <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-white/50">Generate images from your own app with the <span className="font-mono text-[#00F0FF]">captionmotion-ai-image-model</span>. Create API keys, track usage, buy credits and read the docs.</p>
          </div>
          <div className="flex items-center gap-2">
          <button onClick={() => navigate("/developer")} data-testid="open-developer-portal"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#00F0FF] px-5 py-2.5 text-[13px] font-bold text-black transition-transform hover:scale-[1.02]">
            Open Developer Portal <ArrowRight size={15} />
          </button>
          <a href="/docs" className="inline-flex items-center gap-1.5 rounded-full border border-[#00F0FF]/30 px-5 py-2.5 text-[13px] font-bold text-[#00F0FF] transition-colors hover:bg-[#00F0FF]/10" data-testid="open-api-docs">
            API Docs
          </a>
          </div>
        </div>
      </div>
    </div>
  );
}
